import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import find from 'lodash/find';
import { IFooter, IGetPromotionResponse } from '@Hotels-at-Home/hah-fe-core/dist/types';
import { createCETYear } from '~/ui/components/layouts/Footer/helper';
import {
  SignUpUpdates,
  TopFooterLinks,
  OtherHotelSites,
  BottomFooterLinks,
} from '@Hotels-at-Home/hah-fe-core/dist/components';

import { parentHotelCompany, siteName, hotelBrandName, zendeskKey } from '~/constants/siteConfig';
import { LG_WIDTH } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import { useWindowSize } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import { logger, getCookie, sanitizeHtml } from '@Hotels-at-Home/hah-fe-core/dist/utils';

interface IProps {
  footer: IFooter;
  termsAndConditions: string;
  promotion: IGetPromotionResponse;
  fixedBottom?: boolean;
}

const Footer = ({ footer, termsAndConditions, promotion, fixedBottom }: IProps): ReactElement => {
  const getCETYear = useMemo(createCETYear, []);

  const { t } = useTranslation('', { keyPrefix: '' });

  const { width } = useWindowSize();

  const isLessThan960 = width && width <= LG_WIDTH;

  const topFooterWrapper = useRef<HTMLDivElement>(null);

  const bottomFooterWrapper = useRef<HTMLDivElement>(null);

  const [isEmployeeSite, setIsEmployeeSite] = useState(false);

  useEffect(() => {
    if (fixedBottom && topFooterWrapper.current && bottomFooterWrapper.current && !isLessThan960) {
      topFooterWrapper.current.style.marginBottom = `${bottomFooterWrapper.current.offsetHeight}px`;
    } else if (fixedBottom && topFooterWrapper.current && isLessThan960) {
      topFooterWrapper.current.style.marginBottom = '0px';
    }
  }, [width]);

  useEffect(() => {
    setIsEmployeeSite(getCookie('promo-tag') === 'employeesite');
  }, []);

  if (!footer) {
    logger(
      'Unable to get footer data from BO; please check if the "footer" block is correctly configured for this store view',
      { type: 'error' },
    );
  }

  const { bottomFooterLinks, customerSupportLinks, otherHotelSites } = footer || {};
  const privacyPolicy = find(bottomFooterLinks, { name: 'privacy-policy' }) || {};
  const isMarriotSite = true; // enables Marriot cookies scripts
  const shouldShowTC = promotion?.promo_is_active && !(Number(promotion?.promo_days_left) < 0);

  return (
    <footer role="contentinfo" className={fixedBottom ? 'fixed-bottom' : ''}>
      <div className="footer-wrap-top shell-bg" ref={topFooterWrapper}>
        {/* <Cookie /> */}
        {privacyPolicy && <SignUpUpdates privacyPolicy={privacyPolicy} variant="stacked" />}
        {customerSupportLinks && (
          <TopFooterLinks customerSupportLinks={customerSupportLinks} zendeskKey={zendeskKey} />
        )}
      </div>
      <div className="footer-wrap-bottom" ref={bottomFooterWrapper}>
        <div className="footer-bottom" id="footerBottom">
          {otherHotelSites && <OtherHotelSites otherHotelSites={otherHotelSites} />}
          <div className="links copyright clearfix clear font-body-extra-small">
            {bottomFooterLinks && (
              <BottomFooterLinks
                bottomFooterLinksData={bottomFooterLinks}
                isMarriotSite={isMarriotSite}
              />
            )}
            <p className="footer__copyright" id="footer__copyright">
              {(parentHotelCompany as string) === 'Marriott'
                ? t('$marCommon.copyright', { year: `${getCETYear}` })
                : t('$local.copyright', { year: `${getCETYear}`, siteName })}
            </p>
            <p className="icon-hotel-logo" aria-label={`${hotelBrandName} logo`}>
              <span>{hotelBrandName}</span>
            </p>
            <p>Hotels At Home&#174;</p>
            {/* TODO: Below employee site logic is temporary. We should improve on this by getting the terms from the BO and perhaps moving some logic to the core. */}
            {!isEmployeeSite && shouldShowTC && (
              <p
                className="footer-terms-banner"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(termsAndConditions) }}
              />
            )}
            {isEmployeeSite && <p className="footer-terms-banner">{t('$local.employeeTerms')}</p>}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
